/**
 * Authentication Service for Supabase
 *
 * Handles all Supabase Authentication operations with comprehensive error handling
 * and user-friendly error messages for the order management system.
 *
 * Uses login_id → email lookup via secure RPC function for username-based login.
 *
 * @module authServiceSupabase
 */

import { AuthError, User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

/**
 * Custom authentication error type with user-friendly messages
 */
export interface AuthenticationError {
  code: string
  message: string
  originalError?: Error
}

/**
 * Sign-in credentials interface (using login_id instead of email)
 */
export interface SignInCredentials {
  loginId: string
  password: string
}

/**
 * Maps Supabase Auth error codes to user-friendly Japanese messages
 */
export function getAuthErrorMessage(error: AuthError): string {
  const errorMessage = error.message.toLowerCase()

  // Map common error messages to Japanese
  if (errorMessage.includes('invalid login credentials')) {
    return 'ログインIDまたはパスワードが正しくありません'
  }
  if (errorMessage.includes('email not confirmed')) {
    return 'メールアドレスが確認されていません'
  }
  if (errorMessage.includes('user not found')) {
    return 'ユーザーが見つかりません'
  }
  if (errorMessage.includes('too many requests')) {
    return 'ログイン試行回数が多すぎます。しばらくしてから再度お試しください'
  }
  if (errorMessage.includes('network')) {
    return 'ネットワークエラーが発生しました。インターネット接続を確認してください'
  }

  return 'エラーが発生しました。もう一度お試しください'
}

/**
 * Get the table prefix based on environment
 * Note: Tables in this project don't use local_ prefix
 */
function getTablePrefix(): string {
  return ''
}

/**
 * Look up staff by login_id and generate auth email
 * Note: Auth email is {login_id}@staff.internal, NOT the contact mail field
 */
async function getStaffEmailByLoginId(
  loginId: string
): Promise<{ email: string; staffName: string } | null> {
  const tablePrefix = getTablePrefix()

  const { data, error } = await supabase.rpc('get_staff_email_for_login', {
    p_login_id: loginId,
    p_table_prefix: tablePrefix,
  })

  if (error) {
    console.error('Error looking up staff:', error)
    return null
  }

  if (!data || data.length === 0) {
    return null
  }

  // Use the generated auth email format, not the contact mail field
  // This matches how create-staff-auth Edge Function creates the auth user
  return {
    email: `${loginId}@staff.internal`,
    staffName: data[0].staff_name,
  }
}

/**
 * Sign in with login_id and password
 *
 * Flow:
 * 1. Look up email from staffs table using login_id (via secure RPC)
 * 2. Sign in with Supabase Auth using email + password
 *
 * @param credentials - Login ID and password credentials
 * @returns Promise resolving to session and user
 * @throws AuthenticationError with user-friendly message
 */
export async function signIn(credentials: SignInCredentials): Promise<{
  user: User
  session: Session
}> {
  try {
    // Step 1: Look up email by login_id
    const staffInfo = await getStaffEmailByLoginId(credentials.loginId)

    if (!staffInfo || !staffInfo.email) {
      const authError: AuthenticationError = {
        code: 'auth/user-not-found',
        message: 'ログインIDが見つかりません',
      }
      throw authError
    }

    // Step 2: Sign in with Supabase Auth
    const { data, error } = await supabase.auth.signInWithPassword({
      email: staffInfo.email,
      password: credentials.password,
    })

    if (error) {
      const authError: AuthenticationError = {
        code: error.name || 'auth/error',
        message: getAuthErrorMessage(error),
        originalError: error,
      }
      throw authError
    }

    if (!data.user || !data.session) {
      const authError: AuthenticationError = {
        code: 'auth/no-session',
        message: '認証に失敗しました',
      }
      throw authError
    }

    return {
      user: data.user,
      session: data.session,
    }
  } catch (error) {
    // Re-throw if already an AuthenticationError
    if ((error as AuthenticationError).code) {
      throw error
    }

    // Wrap unexpected errors
    const authError: AuthenticationError = {
      code: 'auth/unknown',
      message: 'エラーが発生しました。もう一度お試しください',
      originalError: error as Error,
    }
    throw authError
  }
}

/**
 * Sign out the current user
 *
 * Note: If the session is already invalid (e.g., after admin password change),
 * the sign out may fail server-side but we still want to clear local state.
 */
export async function signOutUser(): Promise<void> {
  try {
    const { error } = await supabase.auth.signOut({ scope: 'local' })

    if (error) {
      // Log but don't throw for session-related errors
      // The user may already be effectively signed out
      console.warn('Sign out warning:', error.message)

      // If session is invalid/expired, that's fine - we're signing out anyway
      if (
        error.message.includes('session') ||
        error.message.includes('token') ||
        error.message.includes('expired') ||
        error.message.includes('invalid')
      ) {
        // Don't throw - consider sign out successful
        return
      }

      // For other errors, still throw
      const authError: AuthenticationError = {
        code: error.name || 'auth/signout-error',
        message: 'ログアウトに失敗しました',
        originalError: error,
      }
      throw authError
    }
  } catch (error) {
    // If error is already our AuthenticationError, re-throw
    if ((error as AuthenticationError).code) {
      throw error
    }
    // For network errors or other issues, log and continue
    console.warn('Sign out error (continuing):', error)
  }
}

/**
 * Get current session
 */
export async function getCurrentSession(): Promise<Session | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<User | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  return user
}

/**
 * Validate login_id format
 */
export function isValidLoginId(loginId: string): boolean {
  // Login ID should be alphanumeric with underscores, 3-50 characters
  const loginIdRegex = /^[a-zA-Z0-9_]{3,50}$/
  return loginIdRegex.test(loginId)
}

/**
 * Validate password strength
 */
export function validatePassword(password: string): {
  isValid: boolean
  message?: string
} {
  if (password.length < 6) {
    return {
      isValid: false,
      message: 'パスワードは6文字以上である必要があります',
    }
  }

  if (password.length < 8) {
    return {
      isValid: true,
      message: 'より強力なパスワードのため、8文字以上を推奨します',
    }
  }

  return { isValid: true }
}
